import axios from "axios";
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    type ReactNode
} from "react";

import axiosClient from "../shared/lib/axiosClient";

// Minimal user shape: add the fields your /web-auth/me endpoint returns
export interface AuthUser {
    id: number | string;
    name: string;
    phone_number: string;
    credit: number;
    streak: number;
    xp: number;
    character?: string;
    session_id?: string;
}

// Response shape of GET /web-auth/me
interface MeResponse {
    success: boolean;
    authenticated: boolean;
    user?: AuthUser;
}

interface AuthContextValue {
    user: AuthUser | null;
    isAuthenticated: boolean;
    loading: boolean;
    refreshUser: () => Promise<AuthUser | null>;
    logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
    const [user, setUser] = useState<AuthUser | null>(null);
    const [loading, setLoading] = useState(true);

    // Asks the API who is logged in and syncs the result into state
    const refreshUser = useCallback(async (): Promise<AuthUser | null> => {
        try {
            const { data } = await axiosClient.get<MeResponse>("/web-auth/me");

            if (data.success && data.authenticated && data.user) {
                setUser(data.user);
                return data.user;
            }
        } catch (error) {
            // An HTTP error response (e.g. 401) just means "not logged in".
            // Only unexpected failures (network, etc.) are worth logging.
            if (!axios.isAxiosError(error) || !error.response) {
                console.error("Failed to check authentication:", error);
            }
        }

        setUser(null);
        return null;
    }, []);

    // Check the session once when the app starts
    useEffect(() => {
        const initializeAuth = async () => {
            setLoading(true);
            await refreshUser();
            setLoading(false);
        };

        initializeAuth();
    }, [refreshUser]);

    const logout = useCallback(async (): Promise<void> => {
        try {
            await axiosClient.post("/web-auth/logout");
        } catch (error) {
            console.error("Logout error:", error);
        } finally {
            // Always clear local state, even if the request failed
            setUser(null);
        }
    }, []);

    // Memoized so consumers only re-render when something actually changes
    const value = useMemo<AuthContextValue>(
        () => ({
            user,
            isAuthenticated: !!user,
            loading,
            refreshUser,
            logout
        }),
        [user, loading, refreshUser, logout]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error("useAuth must be used inside AuthProvider");
    }

    return context;
};